import { router } from "@inertiajs/react"
import { Loader2, Send } from "lucide-react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import { formatDate } from "./status-chip"
import type { CorrespondenceRow } from "./types"

/**
 * The follow-up thread — the evidence behind "when's the last time we followed
 * up?". Outbound on the right, inbound on the left, because that's the shape
 * every person on the team already reads without instruction.
 *
 * A DRAFT IS NOT A MESSAGE. Chaser drafts land here flagged, unsent, with a
 * Mark-as-sent affordance: the product writes the email, a person decides
 * whether it goes. Nothing in this app mails a lessor on its own.
 */
export function CorrespondenceThread({
  correspondences,
  createPath,
  counterpartyTypes,
  canEdit,
  canRequestDispo = false,
}: {
  correspondences: CorrespondenceRow[]
  createPath: string
  counterpartyTypes: string[]
  canEdit: boolean
  /** Open bad order, no disposition requested yet — offer the one-click draft. */
  canRequestDispo?: boolean
}) {
  const [subject, setSubject] = useState("")
  const [body, setBody] = useState("")
  const [counterparty, setCounterparty] = useState(
    counterpartyTypes[0] ?? "lessor",
  )
  const [saving, setSaving] = useState(false)
  const [sendingId, setSendingId] = useState<number | null>(null)
  const [requesting, setRequesting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function submit(event: React.FormEvent) {
    event.preventDefault()
    if (saving || !body.trim()) return
    setSaving(true)
    setError(null)
    router.post(
      createPath,
      {
        correspondence: {
          direction: "outbound",
          counterparty_type: counterparty,
          subject: subject || null,
          body,
        },
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setSubject("")
          setBody("")
        },
        onError: () =>
          setError("Could not save the message — please try again."),
        onFinish: () => setSaving(false),
      },
    )
  }

  // A draft we know an address for is actually delivered over email; one with
  // no recipient is only recorded as sent. That split is the whole "nothing
  // mails a lessor on its own, but a real request does go out" contract.
  function sendDraft(row: CorrespondenceRow) {
    if (sendingId) return
    setSendingId(row.id)
    setError(null)
    const payload = row.to_address
      ? { deliver_id: row.id }
      : { send_id: row.id }
    router.post(createPath, payload, {
      preserveScroll: true,
      onError: () => setError("Could not send — please try again."),
      onFinish: () => setSendingId(null),
    })
  }

  function requestDispo() {
    if (requesting) return
    setRequesting(true)
    setError(null)
    router.post(
      createPath,
      { draft_dispo: 1 },
      {
        preserveScroll: true,
        onError: () =>
          setError("Could not draft the request — please try again."),
        onFinish: () => setRequesting(false),
      },
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle>Correspondence</CardTitle>
        {canEdit && canRequestDispo && (
          <Button
            size="sm"
            variant="outline"
            onClick={requestDispo}
            disabled={requesting}
          >
            {requesting ? (
              <Loader2 className="mr-1 size-4 animate-spin" />
            ) : (
              <Send className="mr-1 size-4" />
            )}
            Request shop disposition
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        )}

        {correspondences.length === 0 && (
          <p className="text-muted-foreground text-sm">
            Nothing sent or received on this work order yet.
          </p>
        )}

        <ul className="space-y-3">
          {correspondences.map((row) => (
            <li
              key={row.id}
              className={cn(
                "flex",
                row.direction === "outbound" ? "justify-end" : "justify-start",
              )}
            >
              <div
                className={cn(
                  "max-w-[85%] rounded-lg border px-3 py-2",
                  row.direction === "outbound"
                    ? "bg-primary/5 border-primary/20"
                    : "bg-muted/50",
                  row.draft &&
                    "border-dashed border-amber-400 bg-amber-50 dark:bg-amber-950/30",
                )}
              >
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  {row.draft && <Badge variant="secondary">Draft</Badge>}
                  {row.counterparty_type && (
                    <span className="text-muted-foreground text-xs capitalize">
                      {row.counterparty_type}
                    </span>
                  )}
                  <span className="text-muted-foreground text-xs">
                    {row.draft
                      ? "not sent"
                      : formatDate(row.sent_at ?? row.created_at)}
                  </span>
                </div>

                {row.subject && (
                  <p className="text-sm font-medium">{row.subject}</p>
                )}
                {row.body && (
                  <p className="mt-0.5 text-sm whitespace-pre-line">
                    {row.body}
                  </p>
                )}

                {row.draft && canEdit && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => sendDraft(row)}
                      disabled={sendingId === row.id}
                    >
                      {sendingId === row.id ? (
                        <Loader2 className="mr-1 size-4 animate-spin" />
                      ) : (
                        <Send className="mr-1 size-4" />
                      )}
                      {row.to_address ? "Send" : "Mark as sent"}
                    </Button>
                    {row.to_address && (
                      <span className="text-muted-foreground text-xs">
                        to {row.to_address}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>

        {canEdit && (
          <form className="space-y-2 border-t pt-4" onSubmit={submit}>
            <div className="flex gap-2">
              <Input
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                placeholder="Subject"
                aria-label="Subject"
                className="h-9"
              />
              <Select value={counterparty} onValueChange={setCounterparty}>
                <SelectTrigger size="sm" className="w-36" aria-label="Sent to">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {counterpartyTypes.map((type) => (
                    <SelectItem key={type} value={type} className="capitalize">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="Log an outbound message…"
              aria-label="Message"
              rows={3}
            />
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={saving || !body.trim()}>
                {saving ? (
                  <Loader2 className="mr-1 size-4 animate-spin" />
                ) : (
                  <Send className="mr-1 size-4" />
                )}
                Log message
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  )
}
